<!--
  One permission gate around the WHOLE tool: the launcher button and the panel.
  Gating only the button left the panel reachable, because ⌘K/Ctrl+K opens it
  without going through the button — a user with no Entity.agentResearchTool got
  the full search UI. Its searches are rejected with 403 by the API, so no data
  came back, but the tool should not be there to use in the first place.

  The keyboard shortcut itself stays ungated on purpose: canUse() resolves a beat
  after page load, so testing it in the handler made the shortcut dead on a fresh
  load. The handler only sets panelVisible, and the panel renders if and when the
  permission resolves true — which fixes that timing problem rather than trading
  it for this hole.
-->
@if (researchService.canUse()) {
  <dx-button
    class="ar-fab"
    icon="search"
    type="default"
    stylingMode="contained"
    hint="Agent research (⌘K / Ctrl+K)"
    (onClick)="researchService.openPanel()">
  </dx-button>

  <dx-popup
    [visible]="researchService.panelVisible()"
    title="Agent research"
    [width]="640"
    [height]="'auto'"
    [maxHeight]="'82vh'"
    [showCloseButton]="true"
    [hideOnOutsideClick]="true"
    [dragEnabled]="true"
    [shading]="true"
    [wrapperAttr]="{ class: 'ar-popup' }"
    (onShown)="onPanelShown()"
    (onHidden)="onPanelHidden()">
    <div *dxTemplate="let data of 'content'" class="ar-panel">

      <div class="ar-search">
        <input
          #termInput
          type="text"
          class="ar-search-input"
          [value]="researchService.term()"
          placeholder="Search by name or email…"
          (input)="researchService.onTermInput($any($event.target).value)"
          (keydown.enter)="researchService.search()" />
        <dx-button class="ar-icon-btn" icon="search" type="default" stylingMode="contained" hint="Search" [disabled]="researchService.loading()" (onClick)="researchService.search()"></dx-button>
        <dx-button class="ar-icon-btn" icon="filter" [type]="researchService.filtersOpen() ? 'default' : 'normal'" stylingMode="outlined" hint="Filters" (onClick)="researchService.toggleFilters()"></dx-button>
      </div>

      @if (researchService.filtersOpen()) {
        <div class="ar-filters">
          <div class="ar-filter-row">
            <span class="ar-filter-label">Fields</span>
            <div class="ar-chips">
              <button type="button" class="ar-chip" [class.ar-chip--on]="researchService.isAuto()" (click)="researchService.setAuto()">Auto</button>
              <button type="button" class="ar-chip" *ngFor="let f of researchService.fieldOptions" [class.ar-chip--on]="researchService.isFieldSelected(f.value)" (click)="researchService.toggleField(f.value)">{{ f.label }}</button>
            </div>
          </div>
          <div class="ar-filter-row">
            <span class="ar-filter-label">Sources</span>
            <div class="ar-chips">
              <button type="button" class="ar-chip" *ngFor="let s of researchService.sourceOptions" [class.ar-chip--on]="researchService.isSourceSelected(s.value)" (click)="researchService.toggleSource(s.value)">{{ s.value | titlecase }}</button>
            </div>
          </div>
          <div class="ar-filter-row">
            <span class="ar-filter-label">Accuracy</span>
            <div class="ar-threshold">
              <input type="range" min="0" max="1" step="0.05" [value]="researchService.threshold()" (input)="researchService.onThresholdChanged($any($event.target).value)" (change)="researchService.onThresholdCommit()" />
              <span class="ar-threshold-val">{{ researchService.threshold() * 100 | number: '1.0-0' }}%</span>
            </div>
          </div>
        </div>
      }

      @if (researchService.error(); as err) {
        <div class="ar-error">{{ err }}</div>
      }

      @if (researchService.loading() && !researchService.response()) {
        <div class="ar-loading">
          <dx-load-indicator [height]="22" [width]="22"></dx-load-indicator><span>Searching…</span>
        </div>
      }

      @if (researchService.response(); as resp) {
        @if (resp.matches.length) {
          <div class="ar-summary">
            {{ resp.matches.length }} possible match{{ resp.matches.length === 1 ? '' : 'es' }} — confirm the right agent from the details below.@if (researchService.loading()) {<span class="ar-updating"> · updating…</span>}
          </div>
        }

        <div class="ar-cards">
          <div class="ar-card" *ngFor="let c of researchService.cards(); trackBy: researchService.trackByKey">
            <div class="ar-card-head">
              <span class="ar-score" [ngClass]="'ar-score--' + c.band">{{ c.scorePercent }}%</span>
              <div class="ar-card-title">
                <div class="ar-name-row">
                  <span class="ar-name">{{ c.name }}</span>
                  @if (c.isOrg) {
                    <span class="ar-org">org</span>
                  }
                  <!--
                    Whether the person is in the carrier's CURRENT downline file.
                    'unknown' shows nothing at all — an absent badge is honest,
                    whereas a grey one would read as a soft "dropped".
                  -->
                  @if (c.production === 'current') {
                    <span class="ar-production ar-production--current" [title]="c.productionHint">
                      <i class="dx-icon dx-icon-check" aria-hidden="true"></i> in production
                    </span>
                  } @else if (c.production === 'dropped') {
                    <span class="ar-production ar-production--dropped" [title]="c.productionHint">
                      <i class="dx-icon dx-icon-clock" aria-hidden="true"></i> not current
                    </span>
                  }
                </div>
                <div class="ar-sub"><span class="ar-src" [ngClass]="'ar-src--' + c.sourceKind">{{ c.sourceLabel }}</span> · matched on {{ c.matchedKind }}</div>
              </div>
              <span class="ar-verdict-pill" [class.ar-verdict-pill--found]="c.agentId != null">
                {{ c.agentId != null ? 'id ' + c.agentId : 'no id yet' }}
              </span>
            </div>
            <div class="ar-facts">
              <span class="ar-fact" *ngFor="let f of c.factItems" [class.ar-fact--muted]="f.muted">
                <b class="ar-fact-label">{{ f.label }}</b> {{ f.value }}
              </span>
              @if (c.facts.length) {
                <button type="button" class="ar-more" (click)="researchService.toggleExpanded(c.key)">
                  {{ c.expanded ? 'less' : 'more' }} <i class="dx-icon" [ngClass]="c.expanded ? 'dx-icon-chevronup' : 'dx-icon-chevrondown'" aria-hidden="true"></i>
                </button>
              }
            </div>
            @if (c.expanded) {
              <div class="ar-detail">
                <div class="ar-detail-fact" *ngFor="let f of c.facts">
                  <span class="ar-detail-kind">{{ f.kind }}</span>
                  <span class="ar-detail-values">{{ f.values.join(' · ') }}</span>
                </div>
              </div>
            }
          </div>
        </div>

        @if (!resp.matches.length && !researchService.loading()) {
          <div class="ar-empty">No match — likely a new agent. Try a different spelling, or widen the accuracy in filters.</div>
        }
      }

      @if (!researchService.response() && !researchService.loading() && !researchService.error()) {
        <div class="ar-hint">
          Search across known agents and carrier downlines.
        </div>
      }
    </div>
  </dx-popup>
}
